// The music library's rules — pure functions, no React.
//
// One source of truth for how a board item becomes a track, how the three
// playlists are cut from the catalog, and how a track becomes a playable URL.
// The screen, the player bar and the tests all read these, so a rebake of the
// catalog can never make the UI disagree with itself.
//
// The AUDIO CONTRACT (binding, phase 2-3):
//   • The catalog is the board (musicBoard.json baked floor / /api/board/music).
//   • The AUDIO lives on Kit's Mac, not in the deployed container. The player
//     asks for it at /api/music-audio/<date>/<file> and degrades to a
//     "local only" state when the backend cannot reach the file yet.
//   * The catalog therefore carries the file REFERENCE (date + file name), and
//     the UI never shows a path or a hash to a human.

export const MOTIVATIONAL = "motivational";
export const BED = "bed";

export const AUDIO_BASE_PATH = "/api/music-audio";

// Today is decided in the portfolio's home zone, whatever the browser says.
export const TZ_DEFAULT = "America/Denver";

// The today source's seam — injectable in tests (the favorites.js pattern).
// Defaults to the real current date in the home zone.
let _todayFn = null;
export const setTodaySource = (fn) => {
  _todayFn = typeof fn === "function" ? fn : null;
};
export const todayIso = () =>
  (_todayFn ? _todayFn() : currentIsoDate(TZ_DEFAULT)) || "";

// ISO dates newer than this stop being part of the library (the tail, not an
// archive — the audio for rolled-off days stays on Kit's machine).
export const CATALOG_DAY_TAIL = 30;

// Board item -> track. The payload carries the truth; the item carries the
// board bookkeeping (slug, state, updatedAt). Jeff's overlay (payload.jeff —
// his favorite hearts) rides along: the Favorites playlist is cut from it.
export const normalizeItems = (items) =>
  (Array.isArray(items) ? items : [])
    .filter((i) => i && i.payload && i.payload.id)
    .map((i) => ({
      ...i.payload,
      slug: i.slug,
      state: i.state,
      updatedAt: i.updatedAt,
      jeff: i.payload.jeff || undefined,
    }))
    .filter((t) => t.title);

// The newest day in the catalog. "Today's Mix" is cut from THIS day, so a
// growing library never mixes yesterday's set into today's.
export const latestDate = (tracks) =>
  (Array.isArray(tracks) ? tracks : []).reduce(
    (max, t) => (!max || (t.date || "") > max ? t.date || "" : max),
    "",
  );

// The REAL current date in a given IANA zone (default America/Denver — the
// portfolio's home zone). The "today" in "Today's Mix" is Jeff's today, not the
// newest day the pipeline happens to have produced: a late render, a missed
// run, or a stale push must NEVER silently rebrand yesterday as today.
export const currentIsoDate = (timeZone = TZ_DEFAULT) => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return "";
  }
};

// ISO date arithmetic in plain days (calendar-safe at the 00:00 boundary).
export const shiftIsoDate = (iso, days) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  d.setUTCDate(d.getUTCDate() + (Number(days) || 0));
  return d.toISOString().slice(0, 10);
};

// ISO date -> "Today", "Yesterday", or a short weekday label. The section
// headers and the day chips both read this, so the catalog's time structure is
// always spelled out in human words.
export const dateLabel = (iso, todayIso) => {
  if (!iso) return "";
  if (todayIso && iso === todayIso) return "Today";
  if (todayIso && iso === shiftIsoDate(todayIso, -1)) return "Yesterday";
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
};

// The distinct days the catalog carries, newest first.
export const catalogDates = (tracks) =>
  Array.from(
    new Set(
      (Array.isArray(tracks) ? tracks : [])
        .map((t) => t.date || "")
        .filter(Boolean),
    ),
  ).sort((a, b) => b.localeCompare(a));

// A playlist's tracks grouped into [{ date, label?, tracks }] sections, newest
// day first — the library list renders one header per section. Tracks with no
// date land in a single unnamed tail section.
export const groupTracksByDate = (tracks) => {
  const sections = [];
  const byDate = new Map();
  for (const t of Array.isArray(tracks) ? tracks : []) {
    const key = t.date || "";
    if (!byDate.has(key)) {
      const section = { date: key, tracks: [] };
      byDate.set(key, section);
      sections.push(section);
    }
    byDate.get(key).tracks.push(t);
  }
  return sections.sort((a, b) => b.date.localeCompare(a.date));
};

// Newest day first, then the board's own order inside a day.
export const byNewestFirst = (a, b) => {
  if ((a.date || "") !== (b.date || "")) {
    return (b.date || "").localeCompare(a.date || "");
  }
  return (a.sort || 0) - (b.sort || 0);
};

export const formatDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export const formatTotalDuration = (tracks) => {
  const s = (tracks || []).reduce((sum, t) => sum + (Number(t.duration_s) || 0), 0);
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
};

// The playable URL for a track. Returns "" when the catalog has no file
// reference (the UI then shows the track as local-only).
export const audioUrl = (track, basePath = AUDIO_BASE_PATH) => {
  if (!track || !track.file) return "";
  const day = encodeURIComponent(track.date || "");
  const file = encodeURIComponent(track.file);
  return `${basePath}/${day}/${file}`;
};

export const isBed = (track) => Boolean(track) && track.track_kind === BED;
export const isMotivational = (track) =>
  Boolean(track) && track.track_kind === MOTIVATIONAL;

// The three playlists, cut from the catalog:
//   • Today's Mix — the REAL current day's motivational set (America/Denver)
//     plus the OPTIONAL beds that ride along with it (the calm reading bed).
//     The two work-session gamma beds stay in Focus; they are started
//     deliberately, not by accident. When the pipeline has not rendered the
//     current day yet, the playlist falls back to the NEWEST day in the
//     catalog but carries isToday=false — the header tells the truth about
//     which day a mix actually is ("Yesterday's mix" never poses as today).
//   • Focus — the beds only, every one of them headphones-required.
//   • Anthems — every motivational track in the library, newest day first.
export const buildPlaylists = (tracks, todayIso = "") => {
  const list = (Array.isArray(tracks) ? tracks : []).slice().sort(byNewestFirst);
  // The mix rides the REAL current day when the catalog has it; otherwise it
  // falls back to the newest day AND SAYS SO (isToday=false).
  const catalogNewest = latestDate(list);
  const mixDate =
    todayIso && list.some((t) => t.date === todayIso) ? todayIso : catalogNewest;
  const isToday = Boolean(todayIso) && mixDate === todayIso;
  const motivational = list.filter(isMotivational);
  const beds = list.filter(isBed);

  const todayMotivational = motivational.filter((t) => t.date === mixDate);
  const todayBeds = beds.filter((t) => t.date === mixDate && t.optional);

  return [
    {
      key: "today",
      label: isToday ? "Today's Mix" : `Mix · ${mixDate}`,
      isToday,
      mixDate,
      description: isToday
        ? "Today's motivational set with the optional calm bed. Start it and the mix plays straight through."
        : "The newest set in the library — today's render has not landed yet.",
      headphones: false,
      tracks: [...(todayMotivational.length ? todayMotivational : motivational), ...todayBeds],
    },
    {
      key: "focus",
      label: "Focus",
      description:
        "Binaural beds for deep work. Headphones required — the beat only lands with them on.",
      headphones: true,
      tracks: beds,
    },
    {
      key: "anthems",
      label: "Anthems",
      description: "Every motivational track in the library.",
      headphones: false,
      tracks: motivational,
    },
  ];
};

export const playlistByKey = (playlists, key) =>
  (playlists || []).find((p) => p.key === key) || (playlists || [])[0] || null;
